import { CSSProperties, RefObject, useCallback, useEffect, useState } from 'react';

interface PositionPlacementProps {
  fieldRef?: RefObject<HTMLDivElement>;
  optionsRef?: RefObject<HTMLDivElement>;
  container_height: number;
  fit_content?: boolean;
  extend?: boolean;
  is_open: boolean;
}

interface PositionPlacement {
  top?: CSSProperties['top'];
  left?: CSSProperties['left'];
  right?: CSSProperties['right'];
  width?: CSSProperties['maxWidth'];
  maxWidth?: CSSProperties['maxWidth'];
  minWidth?: CSSProperties['minWidth'];
}

type Rect = Pick<DOMRect, 'width' | 'left' | 'bottom' | 'top' | 'right'>;

const EMPTY_RECT: Rect = {
  width: 0,
  bottom: 0,
  left: 0,
  top: 0,
  right: 0,
};

export const usePositionPlacement = ({
  fieldRef,
  optionsRef,
  container_height = 0,
  fit_content = false,
  extend = false,
  is_open = false,
}: PositionPlacementProps): PositionPlacement => {
  const [position, setPosition] = useState<PositionPlacement>({});

  const measureDropdown = useCallback(() => {
    const fieldRect = fieldRef?.current?.getBoundingClientRect();
    const optionsRect = optionsRef?.current?.getBoundingClientRect();
    return { field: fieldRect || EMPTY_RECT, options: optionsRect || EMPTY_RECT };
  }, [fieldRef, optionsRef]);

  const calculatePlacement = useCallback(
    ({ field, options }: { field: Rect; options: Rect }) => {
      const innerHeight = window.innerHeight;
      const innerWidth = window.innerWidth;

      const above_field = innerHeight - field.bottom < container_height;

      const extend_width = options.width > field.width ? options.width : field.width;
      const fit_width = options.width;
      const normal_width = field.width;

      const options_width = fit_content ? fit_width : extend ? extend_width : normal_width;
      const right_position = field.left + options_width;
      const pinned_right = right_position > innerWidth;

      return { above_field, options_width, pinned_right, innerWidth };
    },
    [container_height, extend, fit_content]
  );

  useEffect(() => {
    if (!fieldRef?.current || !is_open) return;

    const calculatePosition = () => {
      const { field, options } = measureDropdown();
      const { above_field, options_width, pinned_right } = calculatePlacement({ field, options });

      if (fieldRef) {
        setPosition({
          top: above_field ? field.top - container_height : field.bottom,
          left: pinned_right ? undefined : field.left,
          right: pinned_right ? innerWidth - field.right : undefined,
          minWidth: fit_content ? 'fit-content' : undefined, // calendar component
          width: extend ? 'fit-content' : options_width, // dropdown component
        });
      }
    };

    const resizeObserver = new ResizeObserver(calculatePosition);
    resizeObserver.observe(fieldRef.current);

    const handleScroll = () => {
      requestAnimationFrame(calculatePosition);
    };

    window.addEventListener('scroll', handleScroll, true);
    calculatePosition();

    if (optionsRef?.current) {
      resizeObserver.observe(optionsRef.current);
    }

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
      resizeObserver.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldRef?.current, optionsRef?.current, is_open, container_height, extend]);

  return position;
};
